import React, { useState, useEffect, useCallback } from "react";
import PageWrapperForm from "../../components/PageWrapperForm";
import { useSession } from "hooks/useSession";
import { fetchWrapper } from "utils/apiFetch";

const API_BASE = "https://pro.mokshiq.in";
const PAGE_SIZE = 25;

/*
 * WHAT CHANGED
 * ============
 *
 * 1. THE LIST IS SCOPED SERVER-SIDE NOW.
 *    /agreement_list had no role check whatsoever -- any authenticated user,
 *    including a plain retailer, could call it and receive every acceptance on
 *    the platform: names, phone numbers and IP addresses, across all companies.
 *    company_url narrowed the list but was never a permission check.
 *      Super Admin  -> every acceptance
 *      Main Admin   -> retailers whose main_admin is them
 *      Admin        -> retailers whose admin is them
 *      anyone else  -> 403
 *
 * 2. THE COMPANY DROPDOWN ACTUALLY POPULATES.
 *    setCompanies was never called, so the select only ever had "All
 *    Companies". The list endpoint now returns `companies` -- the distinct
 *    companies inside the caller's own scope, with a count each.
 *
 * 3. THE COMPANY FILTER IS HONOURED.
 *    loadRows hardcoded company_url:"csc.kryntiq.com" and ignored
 *    selectedCompanyUrl, so changing the dropdown did nothing. It was also in
 *    the dependency array while the value it used was not, so the filter could
 *    not have worked even if the value had been read.
 *
 * 4. "VIEW" USES A SIGNED LINK.
 *    The old button built ?user_id=&company_url= client-side. That URL is open:
 *    anyone with it, or anyone editing the number in it, reads that retailer's
 *    name, address, PAN and masked Aadhaar. The button now asks
 *    POST /agreement/link for a signed, expiring URL and opens that.
 */

function StatusBadge({ ok, yes, no }) {
  return (
    <span className={`badge ${ok ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger"}`}>
      {ok ? yes : no}
    </span>
  );
}

const AgreementListPage = () => {
  const { role } = useSession();

  const [companies, setCompanies] = useState([]);
  const [selectedCompanyUrl, setSelectedCompanyUrl] = useState("");

  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [scope, setScope] = useState("");
  const [sendingId, setSendingId] = useState(null);
  const [confirmRow, setConfirmRow] = useState(null);
  const [openingId, setOpeningId] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg, isError = false) => {
    setToast({ msg, isError });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(null), 3200);
  }, []);

  const loadRows = useCallback(async () => {
    setRowsLoading(true);
    try {
      const params = new URLSearchParams();
      // Read the actual selection. Empty means "everything I'm allowed to see",
      // which the server decides -- not the client.
      if (selectedCompanyUrl) params.set("company_url", selectedCompanyUrl);
      if (search) params.set("search", search);
      params.set("page", String(page));
      params.set("page_size", String(PAGE_SIZE));

      const res = await fetchWrapper(`${API_BASE}/agreement_list?${params.toString()}`);

      if (res?.status) {
        setForbidden(false);
        setRows(res.data || []);
        setTotalPages(res.total_pages || 1);
        setTotalCount(res.count || 0);
        setScope(res.scope || "");
        // Only refresh the facets on the unfiltered view, or picking one
        // company would shrink the dropdown to that company alone.
        if (!selectedCompanyUrl && Array.isArray(res.companies)) {
          setCompanies(res.companies);
        }
      } else {
        if (/permitted/i.test(res?.message || "")) setForbidden(true);
        showToast(res?.message || "Failed to load agreements", true);
        setRows([]);
      }
    } catch (e) {
      showToast("Error connecting to server", true);
      setRows([]);
    } finally {
      setRowsLoading(false);
    }
  }, [selectedCompanyUrl, search, page, showToast]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    setPage(1);
  }, [selectedCompanyUrl, search]);

  function handleSearchChange(value) {
    setSearchInput(value);
    window.clearTimeout(handleSearchChange._t);
    handleSearchChange._t = window.setTimeout(() => setSearch(value.trim()), 400);
  }

  // ── open a retailer's agreement through a signed link ────────────────────
  async function openAgreement(row) {
    setOpeningId(row.id);
    try {
      const body = new URLSearchParams();
      body.append("user_id", String(row.user_id));
      if (row.company_url) body.append("company_url", row.company_url);

      const res = await fetchWrapper(`${API_BASE}/agreement/link`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      if (!res?.status || !res?.data?.url) {
        showToast(res?.message || "Could not open the agreement", true);
        return;
      }
      // Opened after the await, so some browsers may treat it as a popup.
      // If your setup blocks it, mint the link on hover instead and put the
      // result in an <a href>.
      window.open(res.data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      showToast("Error connecting to server", true);
    } finally {
      setOpeningId(null);
    }
  }

  // The signed PDF of what they actually accepted.
  async function openDocument(row) {
    setOpeningId(row.id);
    try {
      const body = new URLSearchParams();
      body.append("user_id", String(row.user_id));
      const res = await fetchWrapper(`${API_BASE}/agreement/link`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (!res?.status || !res?.data?.url) {
        showToast(res?.message || "Could not open the document", true);
        return;
      }
      const t = new URL(res.data.url).searchParams.get("t");
      window.open(`${API_BASE}/agreement/document?t=${encodeURIComponent(t)}`,
        "_blank", "noopener,noreferrer");
    } catch (e) {
      showToast("Error connecting to server", true);
    } finally {
      setOpeningId(null);
    }
  }

  async function sendWhatsApp(row) {
    setConfirmRow(null);
    setSendingId(row.id);
    try {
      const body = new URLSearchParams();
      body.append("record_id", String(row.id));
 
      const res = await fetchWrapper(`${API_BASE}/agreement/resend`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
 
      if (res?.status) {
        showToast(res.message || "Sent on WhatsApp");
      } else {
        // Covers the throttle (429), the delivery-off gate (409) and a
        // rejection from Meta. All three carry the sentence that says what to
        // do next, so it is shown verbatim rather than replaced.
        showToast(res?.message || "Could not send on WhatsApp", true);
      }
      loadRows();
    } catch (e) {
      // A fetch wrapper that throws on non-2xx lands here with the body lost.
      showToast("Could not send on WhatsApp. Please try again.", true);
      loadRows();
    } finally {
      setSendingId(null);
    }
  }

  if (forbidden) {
    return (
      <div className="px-3 px-md-4">
        <PageWrapperForm config={{ title: "Agreement Acceptances" }}>
          <div className="alert alert-warning m-3">
            Your role does not have access to agreement records.
          </div>
        </PageWrapperForm>
      </div>
    );
  }

  return (
    <div className="px-3 px-md-4">
      <PageWrapperForm config={{ title: "Agreement Acceptances" }}>
        {/* Filters */}
        <div className="card shadow-sm mb-4">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-12 col-md-4">
                <label className="form-label">Company</label>
                <select
                  className="form-select"
                  value={selectedCompanyUrl}
                  onChange={(e) => setSelectedCompanyUrl(e.target.value)}
                >
                  <option value="">All companies</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.website}>
                      {c.company_name} ({c.total_accepted_users})
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-md-5">
                <label className="form-label">Search</label>
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="bi bi-search" />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by retailer name or phone"
                    value={searchInput}
                    onChange={(e) => handleSearchChange(e.target.value)}
                  />
                </div>
              </div>

              <div className="col-12 col-md-3 text-md-end">
                <span className="badge bg-primary-subtle text-primary px-3 py-2">
                  {totalCount} record{totalCount === 1 ? "" : "s"}
                </span>
                {scope && (
                  <div className="text-muted small mt-1">
                    Showing everything visible to: {scope}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm">
          <div className="card-body p-0">
            {rowsLoading ? (
              <div className="d-flex justify-content-center py-5">
                <div className="spinner-border text-primary" role="status" />
              </div>
            ) : rows.length === 0 ? (
              <div className="alert alert-light text-muted text-center m-3 mb-0">
                No agreement acceptances found.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Retailer</th>
                      <th>Phone</th>
                      {!selectedCompanyUrl && <th>Company</th>}
                      <th>Reference</th>
                      <th>Version</th>
                      <th>Platform</th>
                      <th>Fully Read</th>
                      <th>Declarations</th>
                      <th>WhatsApp</th>
                      <th>IP Address</th>
                      <th>Accepted At</th>
                      <th className="text-end">Agreement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td className="fw-semibold">{r.user_name}</td>
                        <td>{r.user_phone}</td>
                        {!selectedCompanyUrl && <td>{r.company_name}</td>}
                        <td className="text-muted small font-monospace">
                          {r.document_number || "—"}
                        </td>
                        <td>{r.agreement_version}</td>
                        <td>
                          <span className="badge bg-secondary-subtle text-secondary text-capitalize">
                            {r.platform || "—"}
                          </span>
                        </td>
                        <td><StatusBadge ok={r.scrolled_fully} yes="Yes" no="No" /></td>
                        <td>
                          <StatusBadge
                            ok={r.all_declarations_checked}
                            yes="Complete"
                            no="Incomplete"
                          />
                        </td>
                        <td>
                          {r.whatsapp_status === "sent" ? (
                            <span className="badge bg-success-subtle text-success">Sent</span>
                          ) : r.whatsapp_status ? (
                            <span
                              className="badge bg-warning-subtle text-warning text-capitalize"
                              title={r.whatsapp_status}
                            >
                              {r.whatsapp_status.replace(/_/g, " ")}
                            </span>
                          ) : (
                            <span className="text-muted small">—</span>
                          )}
                        </td>
                        <td className="text-muted small">{r.ip_address || "—"}</td>
                        <td className="text-muted small">{r.accepted_at || "—"}</td>
                        <td className="text-end text-nowrap">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary me-1"
                            disabled={openingId === r.id}
                            onClick={() => openAgreement(r)}
                          >
                            <i className="bi bi-eye me-1" />
                            {openingId === r.id ? "Opening…" : "View"}
                          </button>
                          {r.has_pdf && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary me-1"
                              disabled={openingId === r.id}
                              onClick={() => openDocument(r)}
                              title="The signed PDF exactly as accepted"
                            >
                              <i className="bi bi-file-earmark-pdf me-1" />
                              PDF
                            </button>
                          )}
                          {/* Shown only when there is a document to send. Every
                              other rule -- who may send, how often, whether
                              delivery is switched on -- is enforced by the
                              server, which refuses with a readable message. */}
                          {r.has_pdf && (
                            <button
                              type="button"
                              className={`btn btn-sm ${
                                r.whatsapp_status === "sent"
                                  ? "btn-outline-success"
                                  : "btn-success"
                              }`}
                              disabled={sendingId === r.id}
                              title={`Send the signed PDF to ${r.user_phone} on WhatsApp`}
                              onClick={() => setConfirmRow(r)}
                            >
                              <i className="bi bi-whatsapp me-1" />
                              {sendingId === r.id
                                ? "Sending…"
                                : r.whatsapp_status === "sent"
                                ? "Resend"
                                : "Send"}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Pagination */}
        {rows.length > 0 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <span className="text-muted small">
              Page {page} of {totalPages}
            </span>
            <div className="btn-group" role="group">
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </PageWrapperForm>
{/* Confirm before spending. The number is shown, not editable -- see the
          note at the top of this file. */}
      {confirmRow && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: "rgba(0,0,0,.45)", zIndex: 1070 }}
          onClick={() => setConfirmRow(null)}
        >
          <div
            className="card shadow-lg"
            style={{ maxWidth: 460, width: "92%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-body">
              <h5 className="card-title mb-3">
                <i className="bi bi-whatsapp text-success me-2" />
                Send this agreement on WhatsApp?
              </h5>
 
              <dl className="row mb-3 small">
                <dt className="col-5 text-muted fw-normal">Retailer</dt>
                <dd className="col-7 mb-1">{confirmRow.user_name}</dd>
                <dt className="col-5 text-muted fw-normal">WhatsApp number</dt>
                <dd className="col-7 mb-1 font-monospace">{confirmRow.user_phone}</dd>
                <dt className="col-5 text-muted fw-normal">Document</dt>
                <dd className="col-7 mb-1 font-monospace">
                  {confirmRow.document_number || "—"}
                </dd>
              </dl>
 
              <p className="text-muted small mb-3">
                The retailer receives the signed PDF exactly as they accepted it.
                It goes to the number on their profile and nowhere else.
              </p>
 
              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setConfirmRow(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() => sendWhatsApp(confirmRow)}
                >
                  Send now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {toast && (
        <div
          className={`position-fixed bottom-0 start-50 translate-middle-x mb-4 px-4 py-2 rounded text-white ${
            toast.isError ? "bg-danger" : "bg-success"
          }`}
          style={{ zIndex: 1080 }}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default AgreementListPage;